"use client";
import * as React from "react";
import { useRouter } from "next/navigation";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { searchIndex, type SearchIndexItem } from "@/data/search-index";
import { cn } from "@/lib/utils";
import { ExternalLink } from "lucide-react";

interface SearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  className?: string;
}

// Basic fuzzy matcher (client-side) using simple scoring; can replace with Fuse.js if desired
function scoreItem(q: string, item: SearchIndexItem): number {
  if (!q) return 1;
  const query = q.toLowerCase();
  const haystack = [
    item.title,
    item.description,
    ...(item.tags || []),
    item.group,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  let score = 0;
  if (haystack.includes(query)) score += 5;
  // partial word matches
  const parts = query.split(/\s+/);
  for (const p of parts) {
    if (p && haystack.includes(p)) score += 2;
  }
  // prefix weighting
  if (item.title.toLowerCase().startsWith(query)) score += 3;
  return score;
}

export function SearchDialog({
  open,
  onOpenChange,
  className,
}: SearchDialogProps) {
  const router = useRouter();
  const [query, setQuery] = React.useState("");
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  // Shortcut: Cmd+K / Ctrl+K using native event listener
  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().includes("MAC");
      const metaPressed = isMac ? e.metaKey : e.ctrlKey;
      if (metaPressed && e.key.toLowerCase() === "k") {
        e.preventDefault();
        onOpenChange(true);
        setTimeout(() => inputRef.current?.focus(), 10);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onOpenChange]);

  const items = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    const scored = searchIndex.map((item) => ({
      item,
      score: scoreItem(q, item),
    }));
    return scored
      .filter((s) => {
        if (s.score <= 0) return false;
        // Hide generic sections by default unless explicit match in title or tag
        if (s.item.hideByDefault && q === "") return false;
        if (s.item.hideByDefault && q) {
          const directMatch =
            s.item.title.toLowerCase().includes(q) ||
            (s.item.tags || []).some((t) => t.toLowerCase().includes(q));
          if (!directMatch) return false;
        }
        // Hide city entries until user starts typing
        if (s.item.group === "City" && q === "") return false;
        return true;
      })
      .sort(
        (a, b) => b.score - a.score || a.item.title.localeCompare(b.item.title)
      )
      .map((s) => s.item);
  }, [query]);

  const resultsAnnouncement = `${items.length} result${
    items.length === 1 ? "" : "s"
  } for '${query || "all"}'`;

  const handleSelect = (url: string) => {
    onOpenChange(false);
    router.push(url);
    // Optionally highlight target
    if (url.includes("#")) {
      const id = url.split("#").pop()!;
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          el.classList.add("ring-2", "ring-primary", "transition");
          setTimeout(() => {
            el.classList.remove("ring-2", "ring-primary");
          }, 1600);
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 50);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn("p-0 overflow-hidden", className)}
        aria-label="Site search dialog"
      >
        <DialogTitle className="sr-only">Site Search</DialogTitle>
        <Command shouldFilter={false} className="">
          <div className="flex items-center gap-2 px-4 pt-4 pb-2">
            <CommandInput
              ref={inputRef}
              placeholder="Search pages & sections…"
              value={query}
              onValueChange={setQuery}
              aria-label="Search site content"
              className="flex-1"
            />
          </div>
          <CommandList className="max-h-[50vh] overflow-y-auto">
            {/* Live region for screen readers announcing result counts */}
            <div className="sr-only" aria-live="polite" aria-atomic>
              {resultsAnnouncement}
            </div>
            <CommandEmpty>No results found.</CommandEmpty>
            {query === "" && (
              <div className="px-4 py-2 text-xs text-muted-foreground">
                Type to search sections. Use ⌘K anytime.
              </div>
            )}
            <CommandGroup heading="Results">
              {items.map((item) => (
                <CommandItem
                  key={item.id}
                  value={item.title}
                  onSelect={() => handleSelect(item.url)}
                  className="flex flex-col items-start gap-1 py-3"
                  aria-label={`Go to ${item.title}`}
                >
                  <div className="flex w-full items-center justify-between">
                    <span className="font-medium text-foreground">
                      {item.title}
                    </span>
                    <ExternalLink className="h-3 w-3 text-muted-foreground" />
                  </div>
                  <p className="text-xs text-muted-foreground w-full">
                    {item.group} • {item.description}
                  </p>
                  {item.tags && item.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {item.tags.slice(0, 4).map((tag) => (
                        <span
                          key={tag}
                          className="text-[10px] uppercase tracking-wide bg-muted px-1.5 py-0.5 rounded"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
